import {
  Award,
  Bell,
  Calendar,
  CheckCheck,
  ClipboardList,
  CreditCard,
  MessageCircle,
  Trophy,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  getCategoryLabelKey,
  getTabCategories,
  NotificationsList,
  TAB_ALL,
  TAB_READ,
  useNotificationsData,
  useNotificationsUi,
  type NotificationCategory,
} from '@entities/notification'
import { useAuth } from '@shared/auth/authContext'
import { Dialog, Tabs, TabsContent, TabsOverflow, type TabItem } from '@shared/ui'

const CATEGORY_ICON: Record<NotificationCategory, LucideIcon> = {
  Requests: UserPlus,
  Users: Users,
  Payments: CreditCard,
  Chats: MessageCircle,
  Olympiads: Calendar,
  Tasks: ClipboardList,
  Results: Trophy,
  Certificates: Award,
}

const TABS_LIST_CLASS = 'mb-3 w-full'
const LIST_CLASS = 'scrollbar max-h-96 space-y-3 overflow-y-auto pr-1'

export function NotificationsPopup({ container }: { container: HTMLElement | null }) {
  const { t } = useTranslation('admin')
  const { role } = useAuth()
  const { notifications, newNotifications } = useNotificationsData()
  const { open, setOpen } = useNotificationsUi()

  const tabCategories = getTabCategories(role)
  const visibleNotifications = notifications.filter((n) => tabCategories.includes(n.type_display))
  const visibleNew = newNotifications.filter((n) => tabCategories.includes(n.type_display))

  const tabItems: TabItem[] = [
    {
      value: TAB_ALL,
      label: (
        <span className="flex items-center gap-2">
          <Bell className="size-5 shrink-0" />
          {t('notification.all')}
        </span>
      ),
      count: visibleNew.length,
    },
    ...tabCategories.map((cat) => {
      const Icon = CATEGORY_ICON[cat]
      return {
        value: cat,
        label: (
          <span className="flex items-center gap-2">
            <Icon className="size-5 shrink-0" />
            {t(getCategoryLabelKey(cat, role))}
          </span>
        ),
        count: visibleNew.filter((n) => n.type_display === cat).length,
      }
    }),
    {
      value: TAB_READ,
      label: (
        <span className="flex items-center gap-2">
          <CheckCheck className="size-5 shrink-0" />
          {t('notification.read')}
        </span>
      ),
      count: visibleNotifications.length,
    },
  ]

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      container={container}
      className="border-border w-lg translate-x-1/3 border p-4"
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold">{t('notification.nots-popup')}</h2>
      </div>

      <Tabs
        items={tabItems}
        defaultValue={TAB_ALL}
        listClassName={TABS_LIST_CLASS}
        overflow={TabsOverflow.wrap}
      >
        <TabsContent value={TAB_ALL} className={LIST_CLASS}>
          <NotificationsList filter={TAB_ALL} allowedCategories={tabCategories} />
        </TabsContent>
        {tabCategories.map((cat) => (
          <TabsContent key={cat} value={cat} className={LIST_CLASS}>
            <NotificationsList filter={cat} />
          </TabsContent>
        ))}
        <TabsContent value={TAB_READ} className={LIST_CLASS}>
          <NotificationsList filter={TAB_READ} allowedCategories={tabCategories} />
        </TabsContent>
      </Tabs>
    </Dialog>
  )
}
